import { pick } from 'lodash-es'
import type { FC } from 'react'
import { isValidElement } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { toast } from 'react-toastify'
import FluentDatabaseSearch20Regular from '~icons/fluent/database-search-20-regular'
import FluentMoreVertical20Filled from '~icons/fluent/more-vertical-20-filled'
import FluentTextboxSettings20Regular from '~icons/fluent/textbox-settings-20-regular'
import IcRoundFlag from '~icons/ic/round-flag'
import { dialogModerationEdit } from 'src/components/Admin/Moderation/ModerationEditDialog'
import { filterTaskParameter } from 'src/components/ArtworkDetail/ArtworkDetail/ArtworkDetailDeriveButtons'
import { prefillImageToVideoPanel } from 'src/components/GenerationTask/MultiColumnGenerationForm/ImageToVideo/context'
import type { I2vChannelType } from 'src/components/GenerationTask/MultiColumnGenerationForm/ImageToVideo/helper'
import { DEFAULT_I2VPRO_PARAMS } from 'src/components/GenerationTask/MultiColumnGenerationForm/ImageToVideo/helper'
import { assignDefined } from 'src/components/GenerationTask/MultiColumnGenerationForm/ImageToVideo/mergeFields'
import {
  I2V_PRO_FORM_FIELDS,
  type I2vTaskParametersPartial,
  parseI2vTaskParameters,
} from 'src/components/GenerationTask/MultiColumnGenerationForm/ImageToVideo/parser'
import { type I2VProFormArgs } from 'src/components/GenerationTask/MultiColumnGenerationForm/ImageToVideo/types'
import { dialogInspectionForRAC } from 'src/components/Modal/InspectDialog'
import { dialogAppeal } from 'src/components/Report/AppealDialog'
import { dialogReport } from 'src/components/Report/ReportDialog'
import { UIButton } from 'src/components/ui/Button'
import type { UIMenuItemProps } from 'src/components/ui/Menu'
import {
  UIMenu,
  UIMenuItem,
  UIMenuLabel,
  UIMenuSeparator,
  UIMenuTrigger,
} from 'src/components/ui/Menu'
import { UIDialog } from 'src/components/ui/Modal'
import {
  executeMarkArtwork,
  executeUpsertArtwork,
  refetchGetArtwork,
} from 'src/services/apollo/artwork'
import { useMyInfo } from 'src/services/apollo/auth'
import type {
  ArtworkBaseFragment,
  ArtworkDetailFragment,
  DetailedTaskParameters,
} from 'src/services/apollo/generated/graphql'
import { MarkType, RefType } from 'src/services/apollo/generated/graphql'
import { deleteArtwork } from 'src/services/helpers/artwork'
import { isArtworkAppealable } from 'src/utils/artwork'
import { cx } from 'src/utils/cx'
import { formatQs } from 'src/utils/qs'
import { isI2vProTask } from 'src/utils/task'
import { startEventGroup, track } from 'src/utils/tracker'
import { UITooltipHelpIcon } from '../../HelpIcon'
import { trackArtwork } from '../common'
import { dialogEditArtwork } from './EditArtwork'

function firstDefined<T>(
  ...values: Array<T | undefined | null>
): T | undefined {
  for (const value of values) {
    if (value !== undefined && value !== null) return value as T
  }
  return undefined
}

const pickString = (value: unknown) =>
  typeof value === 'string' && value.trim().length > 0 ? value : undefined

const buildI2vProImportPayload = (
  artwork: Pick<ArtworkBaseFragment, 'id' | 'media' | 'prompts' | 'extra'>,
  taskParameters: DetailedTaskParameters,
) => {
  const rawParameters = parseI2vTaskParameters(taskParameters.parameters)
  const fallbackParameters = parseI2vTaskParameters(
    (artwork.extra as { i2vProParameters?: unknown } | undefined)
      ?.i2vProParameters,
  )

  const mergedI2vPro: I2VProFormArgs = {
    ...DEFAULT_I2VPRO_PARAMS,
  }

  const applyI2vPro = (
    source: I2vTaskParametersPartial['i2vPro'] | undefined,
  ) => assignDefined(mergedI2vPro, source, I2V_PRO_FORM_FIELDS)

  applyI2vPro(fallbackParameters.i2vPro)
  applyI2vPro(rawParameters.i2vPro)

  const mediaId = firstDefined(
    rawParameters.i2vPro?.mediaId,
    fallbackParameters.i2vPro?.mediaId,
    rawParameters.mediaId,
    fallbackParameters.mediaId,
  )
  if (!mediaId) return null

  const tailMediaId = firstDefined(
    mergedI2vPro.tailMediaId,
    rawParameters.i2vPro?.tailMediaId,
    fallbackParameters.i2vPro?.tailMediaId,
  )
  mergedI2vPro.tailMediaId = tailMediaId

  if (!mergedI2vPro.prompts || mergedI2vPro.prompts.trim().length === 0) {
    const fallbackPrompts = firstDefined(
      rawParameters.i2vPro?.prompts,
      fallbackParameters.i2vPro?.prompts,
      rawParameters.extra?.naturalPrompts,
      fallbackParameters.extra?.naturalPrompts,
      pickString(
        (artwork.extra as { naturalPrompts?: unknown })?.naturalPrompts,
      ),
      artwork.prompts ?? undefined,
    )
    if (fallbackPrompts !== undefined) {
      mergedI2vPro.prompts = fallbackPrompts
    }
  }

  const artworkWidth =
    typeof artwork.media?.width === 'number' ? artwork.media.width : undefined
  const artworkHeight =
    typeof artwork.media?.height === 'number' ? artwork.media.height : undefined

  const width =
    firstDefined(rawParameters.width, fallbackParameters.width, artworkWidth) ??
    1024
  const height =
    firstDefined(
      rawParameters.height,
      fallbackParameters.height,
      artworkHeight,
    ) ?? 1024
  const priority =
    firstDefined(rawParameters.priority, fallbackParameters.priority) ?? 1000

  const naturalPrompts = firstDefined(
    rawParameters.extra?.naturalPrompts,
    fallbackParameters.extra?.naturalPrompts,
    pickString((artwork.extra as { naturalPrompts?: unknown })?.naturalPrompts),
  )

  const referenceImagePrompts = firstDefined(
    rawParameters.extra?.referenceImagePrompts,
    fallbackParameters.extra?.referenceImagePrompts,
  )

  const extra =
    naturalPrompts !== undefined || referenceImagePrompts !== undefined
      ? {
          ...(naturalPrompts !== undefined ? { naturalPrompts } : {}),
          ...(referenceImagePrompts !== undefined
            ? { referenceImagePrompts }
            : {}),
        }
      : undefined

  const loraMap =
    rawParameters.lora && Object.keys(rawParameters.lora).length > 0
      ? rawParameters.lora
      : fallbackParameters.lora

  const sceneId = firstDefined(
    rawParameters.sceneId,
    fallbackParameters.sceneId,
  )

  const mergedParameters: Record<string, unknown> = {
    width,
    height,
    priority,
    i2vPro: {
      ...mergedI2vPro,
      mediaId,
    },
  }

  if (extra) {
    mergedParameters.extra = extra
  }

  if (sceneId) {
    mergedParameters.sceneId = sceneId
  }

  if (loraMap && Object.keys(loraMap).length > 0) {
    mergedParameters.lora = loraMap
  }

  const mergedTaskParameters: DetailedTaskParameters = {
    ...taskParameters,
    parameters: mergedParameters,
  }

  const channel = firstDefined(
    rawParameters.channel,
    fallbackParameters.channel,
  ) as I2vChannelType | undefined

  return {
    taskParameters: mergedTaskParameters,
    mediaId,
    tailMediaId,
    channel,
  }
}

export const AdminActionWarning = () => {
  return (
    <span className="inline-flex items-center gap-2">
      Admin Actions
      <UITooltipHelpIcon variant="normal">
        <p className="text-sm w-80">
          You are not the author of this artwork. But you are admin so that you
          can manage this item. Operate with caution.
        </p>
      </UITooltipHelpIcon>
    </span>
  )
}

export const setArtworkArchivedState = async (
  artworkId: string,
  target: boolean,
) => {
  try {
    const proceed = await UIDialog.confirm({
      confirmProps: {
        color: 'danger',
      },
      heading: `Set artwork archived state to ${target}`,
      content: `The artwork wont be shown in home page if the artwork marked as archived. But it still can be seen in detail page and user personal page.`,
    })
    if (!proceed) return

    await executeUpsertArtwork({
      id: artworkId,
      input: {
        extra: {
          archived: target,
        },
      },
    })
    toast.success('success')
  } catch (err: any) {
    toast.error(err?.message)
  }
}

const ArtworkActionButton: FC<{
  artwork: ArtworkDetailFragment
  taskParameters?: DetailedTaskParameters
  className?: string
}> = ({ artwork, taskParameters, className }) => {
  const { t } = useTranslation(['artwork', 'common'])
  const { data: userInfo } = useMyInfo()
  const navigate = useNavigate()

  if (!artwork) return null

  const selfArtwork = artwork.authorId === userInfo?.id

  const beingAdmin = userInfo?.isAdmin

  const canAppeal = userInfo && selfArtwork && isArtworkAppealable(artwork)

  const reportArtwork = async () => {
    dialogReport(RefType.Artwork, artwork.id)
  }

  const appealArtwork = async () => {
    dialogAppeal(RefType.Artwork, artwork.id)
  }

  const editArtwork = async () => {
    dialogEditArtwork(artwork)
  }

  const markModerationNsfw = async () => {
    dialogModerationEdit({
      refIds: [artwork.id],
      refType: RefType.Artwork,
      values: {
        ...pick(artwork.flag, ['isRealistic', 'isSensitive', 'isMinors']),
      },
      mutate: () => refetchGetArtwork(artwork.id),
    })
  }
  const showDetailInfos = async () => {
    dialogInspectionForRAC({
      ...artwork,
      task: taskParameters,
    })
  }

  const adminUseAsReference = async () => {
    /**
     * The menu item is gated by `isI2vProTask(taskParameters)`, so this
     * handler only runs when `taskParameters` is non-null and contains an
     * `i2vPro` payload. Re-check defensively so the type narrows for
     * downstream calls and any future caller of this closure stays safe.
     */
    if (!taskParameters) {
      toast.error('Task parameters unavailable for this artwork')
      return
    }

    try {
      trackArtwork('use_as_refer_btn_click', artwork, {
        source: 'admin-menu',
      })

      const sanitizedTaskParameters =
        (await filterTaskParameter(taskParameters)) ?? taskParameters

      const i2vProPayload = buildI2vProImportPayload(
        artwork,
        sanitizedTaskParameters,
      )

      if (!i2vProPayload) {
        toast.error('Unable to import i2v parameters for this artwork')
        return
      }

      const goToUrl = `/generator/image/animation?${formatQs({
        importType: 'use_as_reference',
        mediaId: i2vProPayload.mediaId,
        tailMediaId: i2vProPayload.tailMediaId,
        channel: i2vProPayload.channel,
      })}`

      trackArtwork('use_as_refer', artwork, { source: 'admin-menu' })

      await executeMarkArtwork({
        artworkId: artwork.id,
        type: MarkType.Inspire,
        target: true,
      })

      prefillImageToVideoPanel({ task: i2vProPayload.taskParameters })

      startEventGroup('generate_source')
      navigate(goToUrl)
    } catch (err: any) {
      toast.error(err?.message)
    }
  }

  const menuItems = [
    {
      id: 'explore',
      onAction: async () => {
        trackArtwork('explore_this_style_btn_click', artwork)
        navigate(`/artwork/${artwork.id}/relevant`)
      },
      children: t('artwork:explore-this-style'),
    },
    // userInfo not null: only show report when logged in
    userInfo &&
      !selfArtwork && {
        id: 'report',
        onAction: reportArtwork,
        children: t('artwork:action.report'),
        icon: <IcRoundFlag />,
      },
    canAppeal && {
      id: 'appeal',
      onAction: appealArtwork,
      icon: <IcRoundFlag />,
      children: t('common:appeal.action-label'),
    },
    ...(!selfArtwork && beingAdmin
      ? [
          <UIMenuSeparator key="admin-override-actions-separator" />,
          <UIMenuLabel
            className="flex items-center gap-4 font-bold"
            key="subheader-admin-override-actions"
          >
            <span>Admin (override) Actions</span>
          </UIMenuLabel>,
        ]
      : []),
    ...(selfArtwork || beingAdmin
      ? [
          {
            id: 'edit',
            onAction: editArtwork,
            children: t('artwork:action.edit'),
            icon: <FluentTextboxSettings20Regular className="size-5" />,
          },
          {
            id: 'delete',
            onAction: async () => {
              track('delete_click')
              await deleteArtwork(artwork.id)
            },
            color: 'danger',
            children: t('artwork:action.delete'),
          },
        ]
      : []),
    ...(beingAdmin
      ? [
          <UIMenuSeparator key="admin-actions-separator" />,
          <UIMenuLabel
            className="flex items-center gap-4 font-bold text-orange-600 dark:text-orange-500"
            key="subheader-admin-moderation"
          >
            <AdminActionWarning />
          </UIMenuLabel>,
        ]
      : []),
    ...(beingAdmin
      ? [
          {
            id: 'mark-moderation-sfw',
            color: 'admin',
            onAction: markModerationNsfw,
            children: 'Adjust Artwork Moderation',
          },
          {
            id: 'inspect',
            color: 'admin',
            onAction: showDetailInfos,
            children: 'Inspect',
            icon: <FluentDatabaseSearch20Regular />,
          },
          isI2vProTask(taskParameters) && {
            id: 'admin-use-as-reference',
            color: 'admin',
            onAction: adminUseAsReference,
            children: `${t('artwork:use-as-reference.label')} (i2v)`,
          },
          {
            id: 'archive',
            onAction: () => {
              setArtworkArchivedState(artwork.id, !artwork.extra?.archived)
            },
            color: 'admin',
            children: (
              <span>
                {artwork.extra?.archived
                  ? 'Unarchive artwork'
                  : 'Archive artwork'}
              </span>
            ),
          },
        ]
      : []),
  ].filter(v => !!v) as (UIMenuItemProps | JSX.Element)[]

  return (
    <UIMenuTrigger>
      <UIButton
        variant="ghost"
        className={cx(className, 'inline-grid place-items-center')}
      >
        <FluentMoreVertical20Filled className={'size-5'} />
      </UIButton>
      <UIMenu placement="bottom end" items={menuItems}>
        {item => {
          if ('id' in item) return <UIMenuItem {...item} />
          if (isValidElement(item)) return item
        }}
      </UIMenu>
    </UIMenuTrigger>
  )
}

export default ArtworkActionButton
